import type { StoreDto } from '@cartly/shared';
import {
  Alert,
  BarList,
  Button,
  Card,
  ChartCard,
  EmptyState,
  PageHeader,
  Spinner,
  StackedBar,
  StatTile,
  TimeSeriesChart,
  cx,
  useToast,
} from '@cartly/ui';
import {
  Bot,
  Check,
  MessageCircle,
  Percent,
  PhoneCall,
  ReceiptText,
  ShoppingBag,
  ShoppingCart,
  TrendingUp,
  UserPlus,
  Wallet,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api/client';
import type { Attribution } from '../api/types';
import { StatusBadge } from '../components/Common';
import { formatMoney, formatRelative, personName } from '../app/format';
import { useLoader } from '../app/hooks';
import { useActiveSession } from '../app/session';
import { PLATFORM_LABELS } from './stores/platforms';

interface Kpi<T> {
  value: T;
  previous: T;
}

interface Dashboard {
  range: { days: number; from: string; to: string; timezone: string; currency: string };
  kpis: {
    revenue: Kpi<string>;
    orders: Kpi<number>;
    averageOrderValue: Kpi<string | null>;
    newCustomers: Kpi<number>;
    abandonedCheckouts: Kpi<number>;
    recoveredRevenue: Kpi<string> & { orders: number };
    checkoutConversion: Kpi<number | null>;
    totalStores: number;
    totalCustomers: number;
    otherCurrencyOrders: number;
  };
  series: {
    date: string;
    revenue: string;
    orders: number;
    newCustomers: number;
    abandoned: number;
  }[];
  paymentMix: { method: 'cod' | 'prepaid' | 'other'; orders: number; revenue: string }[];
  cartOutcomes: {
    status: 'abandoned' | 'recovered' | 'completed' | 'open';
    count: number;
    value: string;
  }[];
  topProducts: { title: string; quantity: number; revenue: string }[];
  stores: {
    id: string;
    name: string;
    platform: StoreDto['platform'];
    status: StoreDto['status'];
    lastEventAt: string | null;
    orders: number;
    revenue: string;
    failedEvents: number;
  }[];
  recentOrders: {
    id: string;
    orderNumber: string;
    store: string;
    customer: { firstName: string | null; lastName: string | null; email: string | null } | null;
    total: string;
    currency: string;
    financialStatus: string;
    paymentMethod: string;
    placedAt: string;
    cancelled: boolean;
  }[];
}

interface Setup {
  setup: {
    emailVerified: boolean;
    mfaEnabled: boolean;
    hasTeammates: boolean;
    storeConnected: boolean;
    whatsappConnected: boolean;
    templateApproved: boolean;
    automationLive: boolean;
  };
  team: { members: number; pendingInvitations: number };
}

const RANGES = [7, 30, 90] as const;
const UPCOMING = [
  { label: 'WhatsApp messages', icon: MessageCircle, phase: 4 },
  { label: 'Automation executions', icon: Zap, phase: 5 },
  { label: 'AI assistant replies', icon: Bot, phase: 6 },
  { label: 'AI calls', icon: PhoneCall, phase: 7 },
];
const PAYMENT_LABELS = { cod: 'Cash on delivery', prepaid: 'Prepaid', other: 'Other' } as const;
const CART_LABELS = {
  abandoned: 'Abandoned',
  recovered: 'Recovered',
  completed: 'Completed',
  open: 'Still open',
} as const;

/** Relative change; null when there is nothing to compare against. */
const change = (now: number, prev: number | null) =>
  prev === null || prev === 0 ? (now === 0 ? 0 : null) : (now - prev) / prev;
const dayLabel = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const keyLabel = (key: string) => dayLabel.format(new Date(`${key}T12:00:00Z`));

export function OverviewPage() {
  const session = useActiveSession();
  const toast = useToast();
  const tenantId = session.activeTenant?.id;
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [storeId, setStoreId] = useState('');
  const query = `days=${days}${storeId ? `&storeId=${storeId}` : ''}`;
  const dash = useLoader(
    () => api.get<Dashboard>(`/analytics/dashboard?${query}`),
    `${tenantId}:${query}`,
  );
  const attribution = useLoader(
    () => api.get<Attribution>(`/analytics/attribution?${query}`),
    `${tenantId}:${query}`,
  );
  const setup = useLoader(() => api.get<Setup>('/overview'), tenantId);
  const stores = useLoader(
    () => api.get<{ items: StoreDto[] }>('/stores').then((r) => r.items),
    tenantId,
  );
  // Keep the previous render on screen (dimmed) while a new range loads.
  const [last, setLast] = useState<Dashboard | null>(null);
  if (dash.data && dash.data !== last) setLast(dash.data);
  const d = dash.data ?? last;

  const resendVerification = async () => {
    try {
      await api.post('/auth/verify-email/resend');
      toast.success('Verification email sent');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const cur = d?.range.currency ?? 'INR';
  const fm = (v: string | number | null) => (v === null ? '—' : formatMoney(String(v), cur));
  /** Headline figures in whole currency units. */
  const fw = (v: string | number | null) =>
    v === null ? '—' : formatMoney(String(v), cur, { whole: true });
  const series = d?.series ?? [];

  const steps = setup.data
    ? [
        {
          done: setup.data.setup.emailVerified,
          label: 'Verify your email address',
          action: (
            <Button size="sm" onClick={() => void resendVerification()}>
              Resend email
            </Button>
          ),
        },
        {
          done: setup.data.setup.storeConnected,
          label: 'Connect your first store',
          action: (
            <Link to="/stores" className="btn btn--secondary btn--sm">
              Connect
            </Link>
          ),
        },
        {
          done: setup.data.setup.whatsappConnected,
          label: 'Connect WhatsApp',
          action: (
            <Link to="/whatsapp" className="btn btn--secondary btn--sm">
              Connect
            </Link>
          ),
        },
        {
          done: setup.data.setup.templateApproved,
          label: 'Get a message template approved',
          action: (
            <Link to="/whatsapp/templates" className="btn btn--secondary btn--sm">
              Templates
            </Link>
          ),
        },
        {
          done: setup.data.setup.automationLive,
          label: 'Turn on an automation (cart recovery, COD confirmation)',
          action: (
            <Link to="/automations" className="btn btn--secondary btn--sm">
              Automations
            </Link>
          ),
        },
        {
          done: setup.data.setup.mfaEnabled,
          label: 'Turn on two-factor authentication',
          action: (
            <Link to="/settings/security" className="btn btn--secondary btn--sm">
              Set up
            </Link>
          ),
        },
        {
          done: setup.data.setup.hasTeammates,
          label: 'Invite your team',
          action: (
            <Link to="/settings/members" className="btn btn--secondary btn--sm">
              Invite
            </Link>
          ),
        },
      ]
    : [];
  const remaining = steps.filter((s) => !s.done).length;

  return (
    <div className="dashboard">
      <PageHeader
        title={`Welcome, ${session.user.name.split(' ')[0]}`}
        description={`Here's how ${session.activeTenant?.name ?? 'your business'} is doing.`}
      />

      {/* One filter row scopes every number and chart below. */}
      <div className="dashboard__filters" role="group" aria-label="Dashboard filters">
        <div className="segmented" role="radiogroup" aria-label="Date range">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={days === r}
              className={cx('segmented__option', days === r && 'segmented__option--active')}
              onClick={() => setDays(r)}
            >
              Last {r} days
            </button>
          ))}
        </div>
        <select
          className="select select--inline"
          aria-label="Store"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
        >
          <option value="">All stores</option>
          {(stores.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {d && <span className="text-muted text-sm">Times in {d.range.timezone}</span>}
      </div>

      {remaining > 0 && (
        <Card
          title="Get started"
          subtitle={`${steps.length - remaining} of ${steps.length} done`}
          className="dashboard__setup"
        >
          <ul className="setup-row">
            {steps.map((s) => (
              <li
                key={s.label}
                className={cx('setup-row__item', s.done && 'setup-row__item--done')}
              >
                <span className={cx('checklist__status', s.done && 'checklist__status--done')}>
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="setup-row__label">{s.label}</span>
                {!s.done && s.action}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {dash.error && <Alert tone="critical">{dash.error}</Alert>}
      {!d ? (
        <div className="page-loading">
          <Spinner size="lg" label="Loading dashboard" />
        </div>
      ) : (
        <div className={cx('dashboard__content', dash.loading && 'dashboard__content--refreshing')}>
          <div className="kpi-grid kpi-grid--dashboard">
            <StatTile
              label="Revenue"
              icon={<Wallet size={16} />}
              value={fw(d.kpis.revenue.value)}
              delta={{
                change: change(Number(d.kpis.revenue.value), Number(d.kpis.revenue.previous)),
              }}
              trend={series.map((p) => Number(p.revenue))}
              hint={
                d.kpis.otherCurrencyOrders > 0
                  ? `${d.kpis.otherCurrencyOrders} orders in other currencies not included`
                  : undefined
              }
            />
            <StatTile
              label="Orders"
              icon={<ShoppingBag size={16} />}
              value={d.kpis.orders.value.toLocaleString()}
              delta={{ change: change(d.kpis.orders.value, d.kpis.orders.previous) }}
              trend={series.map((p) => p.orders)}
            />
            <StatTile
              label="Average order value"
              icon={<ReceiptText size={16} />}
              value={fw(d.kpis.averageOrderValue.value)}
              delta={{
                change:
                  d.kpis.averageOrderValue.value === null
                    ? null
                    : change(
                        Number(d.kpis.averageOrderValue.value),
                        d.kpis.averageOrderValue.previous === null
                          ? null
                          : Number(d.kpis.averageOrderValue.previous),
                      ),
              }}
            />
            <StatTile
              label="New customers"
              icon={<UserPlus size={16} />}
              value={d.kpis.newCustomers.value.toLocaleString()}
              delta={{ change: change(d.kpis.newCustomers.value, d.kpis.newCustomers.previous) }}
              trend={series.map((p) => p.newCustomers)}
              hint={`${d.kpis.totalCustomers.toLocaleString()} customers in total`}
            />
            <StatTile
              label="Checkout conversion"
              icon={<Percent size={16} />}
              value={
                d.kpis.checkoutConversion.value === null
                  ? '—'
                  : `${(d.kpis.checkoutConversion.value * 100).toFixed(1)}%`
              }
              hint={
                d.kpis.checkoutConversion.value === null
                  ? 'Needs cart or checkout events'
                  : 'Carts and checkouts that became orders'
              }
            />
            <StatTile
              label="Recovered revenue"
              icon={<TrendingUp size={16} />}
              value={fw(d.kpis.recoveredRevenue.value)}
              delta={{
                change: change(
                  Number(d.kpis.recoveredRevenue.value),
                  Number(d.kpis.recoveredRevenue.previous),
                ),
              }}
              hint={`${d.kpis.recoveredRevenue.orders} orders from abandoned carts`}
            />
            {attribution.data && (
              <StatTile
                label="COD confirmations"
                icon={<Check size={16} />}
                value={String(attribution.data.cod.confirmed)}
                hint={`confirmed · ${attribution.data.cod.declined} declined · ${attribution.data.cod.pending} waiting · ${attribution.data.cod.paidViaLink} paid via link`}
              />
            )}
            {attribution.data && (
              <StatTile
                label="Earned with Cartly"
                icon={<MessageCircle size={16} />}
                value={fw(attribution.data.attributedRevenue)}
                hint={
                  attribution.data.attributedOrders === 0
                    ? `No orders within ${attribution.data.windowDays} days of a flow or campaign message yet`
                    : `${attribution.data.attributedOrders} orders${
                        attribution.data.share === null
                          ? ''
                          : ` (${attribution.data.share}% of revenue)`
                      } placed within ${attribution.data.windowDays} days of a delivered message`
                }
              />
            )}
          </div>

          <div className="dashboard__grid">
            <ChartCard
              className="span-8"
              title="Revenue"
              subtitle={`Daily, ${cur}, excluding cancelled orders`}
              headline={fw(d.kpis.revenue.value)}
              table={{
                columns: ['Date', 'Revenue', 'Orders'],
                rows: series.map((p) => [keyLabel(p.date), fm(p.revenue), p.orders]),
              }}
            >
              <TimeSeriesChart
                kind="area"
                height={330}
                data={series.map((p) => ({ key: p.date, value: Number(p.revenue) }))}
                formatValue={(v) => formatMoney(String(v), cur, { whole: true })}
                formatKey={keyLabel}
                ariaLabel={`Daily revenue for the last ${days} days`}
              />
            </ChartCard>

            <div className="span-4 dashboard__stack">
              <ChartCard
                className="chart-card--compact"
                title="Payment methods"
                subtitle="Share of orders"
                table={{
                  columns: ['Method', 'Orders', 'Revenue'],
                  rows: d.paymentMix.map((m) => [
                    PAYMENT_LABELS[m.method],
                    m.orders,
                    fm(m.revenue),
                  ]),
                }}
              >
                <StackedBar
                  emptyMessage="No orders in this period"
                  segments={d.paymentMix.map((m, i) => ({
                    label: PAYMENT_LABELS[m.method],
                    value: m.orders,
                    display: `${m.orders.toLocaleString()} · ${fm(m.revenue)}`,
                    slot: (i + 1) as 1 | 2 | 3,
                  }))}
                />
              </ChartCard>

              <ChartCard
                className="chart-card--compact"
                title="Carts & checkouts"
                subtitle="By outcome, active in this period"
                table={{
                  columns: ['Outcome', 'Carts', 'Value'],
                  rows: d.cartOutcomes.map((c) => [CART_LABELS[c.status], c.count, fm(c.value)]),
                }}
              >
                <BarList
                  emptyMessage="No cart or checkout events yet"
                  items={d.cartOutcomes.map((c) => ({
                    label: CART_LABELS[c.status],
                    value: c.count,
                    display: c.count.toLocaleString(),
                    note: fm(c.value),
                  }))}
                />
              </ChartCard>
            </div>

            <ChartCard
              className="span-6"
              title="Orders per day"
              headline={d.kpis.orders.value.toLocaleString()}
              table={{
                columns: ['Date', 'Orders'],
                rows: series.map((p) => [keyLabel(p.date), p.orders]),
              }}
            >
              <TimeSeriesChart
                kind="column"
                height={200}
                data={series.map((p) => ({ key: p.date, value: p.orders }))}
                formatValue={(v) => `${v} ${v === 1 ? 'order' : 'orders'}`}
                formatKey={keyLabel}
                ariaLabel={`Orders per day for the last ${days} days`}
              />
            </ChartCard>

            <ChartCard
              className="span-6"
              title="Top products"
              subtitle="By revenue"
              table={{
                columns: ['Product', 'Units', 'Revenue'],
                rows: d.topProducts.map((p) => [p.title, p.quantity, fm(p.revenue)]),
              }}
            >
              <BarList
                emptyMessage="No products sold in this period"
                items={d.topProducts.map((p) => ({
                  label: p.title,
                  value: Number(p.revenue),
                  display: fm(p.revenue),
                  note: `${p.quantity} sold`,
                }))}
              />
            </ChartCard>

            <Card
              title="Stores"
              className="span-6 self-start"
              flush
              actions={
                <Link to="/stores" className="text-sm">
                  View all
                </Link>
              }
            >
              {d.stores.length === 0 ? (
                <EmptyState
                  icon={<ShoppingCart size={22} />}
                  title="No stores connected"
                  action={
                    <Link to="/stores" className="btn btn--primary btn--sm">
                      Connect a store
                    </Link>
                  }
                >
                  Connect WooCommerce or your own platform to see sales here.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Store</th>
                        <th>Status</th>
                        <th className="table__actions">Orders</th>
                        <th className="table__actions">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.stores.map((s) => (
                        <tr key={s.id}>
                          <td>
                            <Link to={`/stores/${s.id}`} className="table__primary">
                              {s.name}
                            </Link>
                            <div className="table__secondary">
                              {PLATFORM_LABELS[s.platform]} ·{' '}
                              {s.lastEventAt
                                ? `last event ${formatRelative(s.lastEventAt)}`
                                : 'no events yet'}
                            </div>
                          </td>
                          <td>
                            <div className="row">
                              <StatusBadge
                                status={s.status}
                                label={s.status === 'pending' ? 'awaiting events' : undefined}
                              />
                              {s.failedEvents > 0 && (
                                <StatusBadge status="failed" label={`${s.failedEvents} failed`} />
                              )}
                            </div>
                          </td>
                          <td className="table__actions num">{s.orders.toLocaleString()}</td>
                          <td className="table__actions num">{fm(s.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card
              title="Recent orders"
              className="span-6"
              flush
              actions={
                <Link to="/orders" className="text-sm">
                  View all
                </Link>
              }
            >
              {d.recentOrders.length === 0 ? (
                <EmptyState icon={<ShoppingBag size={22} />} title="No orders yet">
                  Orders appear as soon as a connected store sends them.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <tbody>
                      {d.recentOrders.map((o) => (
                        <tr key={o.id}>
                          <td>
                            <div className="table__primary">{o.orderNumber}</div>
                            <div className="table__secondary">
                              {personName(o.customer)} · {o.store}
                            </div>
                          </td>
                          <td>
                            <div className="row">
                              {(o.cancelled || o.financialStatus !== 'unknown') && (
                                <StatusBadge
                                  status={o.cancelled ? 'voided' : o.financialStatus}
                                  label={o.cancelled ? 'cancelled' : undefined}
                                />
                              )}
                              {o.paymentMethod === 'cod' && (
                                <StatusBadge status="open" label="COD" />
                              )}
                            </div>
                          </td>
                          <td className="text-muted text-sm nowrap">
                            {formatRelative(o.placedAt)}
                          </td>
                          <td className="table__actions num">
                            {formatMoney(o.total, o.currency, { whole: true })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card
              title="Coming next"
              subtitle="These figures appear here as each module goes live."
              className="span-12"
            >
              <ul className="upcoming">
                {UPCOMING.map((u) => {
                  const Icon = u.icon;
                  return (
                    <li key={u.label} className="upcoming__item">
                      <span className="kpi__icon">
                        <Icon size={16} />
                      </span>
                      <span>{u.label}</span>
                      <span className="badge badge--info">Phase {u.phase}</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
